import { useState, useEffect, useContext } from "react";
import * as func from "../../../api/apiHelper";
import * as urls from "../../../api/urlHelper";
import ConfirmationPopup from "../../deletePopup/deletePopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SupportContext } from "../../context/supportContext";
import {
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faSearch,
  faEye,
  faClock,
  faCheckCircle,
  faBan,
  faHourglassHalf,
  faHeadset,
  faSlidersH,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const STATUS_MAP = {
  INITIATED: { cls: "bg-yellow-100 text-yellow-700", icon: faClock, label: "Initiated" },
  INPROGRESS: { cls: "bg-blue-100 text-blue-700", icon: faHourglassHalf, label: "In Progress" },
  CLOSEDBYADMIN: { cls: "bg-green-100 text-green-700", icon: faCheckCircle, label: "Closed" },
  WITHDRAWN: { cls: "bg-red-100 text-red-600", icon: faBan, label: "Withdrawn" },
};

const SupportListing = () => {
  const {
    supportSearchTerm: globalSearchTerm,
    setSupportSearchTerm: setGlobalSearchTerm,
    setSupportFilters: setGlobalFilters,
    supportFilters: globalFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
  } = useContext(SupportContext);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteRequestData, setDeleteRequestData] = useState({});
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editRequestData, setEditRequestData] = useState({});
  const [showEditButton, setShowEditButton] = useState(false);
  const [viewRequestPopup, setViewRequestPopup] = useState(false);
  const [viewRequestData, setViewRequestData] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      const params = { page: currentPage, limit, search: globalSearchTerm };
      const res = await func.post(urls.GET_ALL_SUPPORT_REQUESTS, globalFilters, { params });
      if (res?.status === 200) {
        setRequests(res?.data?.data || []);
        setTotalPages(res?.data?.pagination?.totalPages || 1);
      } else {
        toast.error("Failed to load support requests");
      }
      setLoading(false);
    };
    fetchRequests();
  }, [reload, currentPage]);

  const handleSearch = () => { setReload(!reload); setCurrentPage(1); };

  const applyFilters = () => { setReload(!reload); setShowFilter(false); setGlobalSearchTerm(""); setCurrentPage(1); };

  const resetFilters = () => {
    setGlobalFilters({ fullName: "", dateOfBirth: "", _phoneNumber: "", residentLocation: "", status: "", startDate: "", endDate: "", updatedAt: "" });
    setGlobalSearchTerm("");
    setReload(!reload);
    setCurrentPage(1);
  };

  const handleAccept = async () => { setShowDeleteButton(false); };

  const handleEditRequest = async () => {
    if (!editRequestData._id) { toast.warn("ID required"); return; }
    if (!editRequestData._status) { toast.warn("Status required"); return; }
    if (editRequestData._notes && editRequestData._notes.length > 300) { toast.warn("Notes max 300 chars"); return; }
    const payload = { id: editRequestData._id, newStatus: editRequestData._status, notes: editRequestData._notes || "" };
    const res = await func.put(urls.UPDATE_SUPPORT_REQUEST_STATUS, payload);
    if (res?.status === 200) {
      toast.success(res?.data?.message || "Status updated successfully");
      setShowEditPopup(false);
      setReload(!reload);
    } else {
      toast.warn("Failed to update status");
    }
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faHeadset} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Support Requests</h1>
              <p className="text-white/70 text-sm">User support tickets and requests</p>
            </div>
          </div>
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
          >
            <FontAwesomeIcon icon={faSlidersH} />
            Filters
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={globalSearchTerm}
              onChange={(e) => { setGlobalSearchTerm(e.target.value); if (!e.target.value.trim()) handleSearch(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search support requests..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Name / DOB / Type", "Mobile / Location", "Status", "Created / Updated", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faHeadset} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No support requests found
                    </td>
                  </tr>
                ) : (
                  requests.map((req, i) => {
                    const statusCfg = STATUS_MAP[req?._status] || { cls: "bg-gray-100 text-gray-500", label: req?._status };
                    return (
                      <tr key={req._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{req.userId?.fullName || "—"}</p>
                          <p className="text-xs text-gray-400">{req.userId?.dateOfBirth ? new Date(req.userId.dateOfBirth).toLocaleDateString("en-IN") : ""}</p>
                          <p className="text-xs text-gray-400">{req.type || ""}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-700">{req.userId?._phoneNumber || "—"}</p>
                          <p className="text-xs text-gray-400">{req.userId?.residentLocation || ""}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
                            {statusCfg.icon && <FontAwesomeIcon icon={statusCfg.icon} className="text-[10px]" />}
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          <p>{new Date(req.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                          <p className="text-gray-400">{new Date(req.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => { setViewRequestData(req); setViewRequestPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faEye} />
                              View
                            </button>
                            <button
                              onClick={() => { setEditRequestData(req); setShowEditPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      {/* Delete Popup */}
      <ConfirmationPopup
        show={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onCancel={() => setShowDeletePopup(false)}
        onAccept={handleAccept}
        showDeleteButton={showDeleteButton}
        question="Are you sure you want to delete this request?"
        title="Delete Confirmation"
        acceptText="Yes, Delete"
        cancelText="Cancel"
      />

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Filter Requests</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name</label>
                <input type="text" value={globalFilters.fullName} onChange={(e) => setGlobalFilters({ ...globalFilters, fullName: e.target.value })} placeholder="Name" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                <input type="text" value={globalFilters._phoneNumber} onChange={(e) => setGlobalFilters({ ...globalFilters, _phoneNumber: e.target.value })} placeholder="Mobile" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                <input type="date" value={globalFilters.dateOfBirth} onChange={(e) => setGlobalFilters({ ...globalFilters, dateOfBirth: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                <input type="text" value={globalFilters.residentLocation} onChange={(e) => setGlobalFilters({ ...globalFilters, residentLocation: e.target.value })} placeholder="Location" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={globalFilters.startDate || ""} onChange={(e) => setGlobalFilters({ ...globalFilters, startDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input type="date" value={globalFilters.endDate || ""} onChange={(e) => setGlobalFilters({ ...globalFilters, endDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Updated At</label>
                <input type="date" value={globalFilters.updatedAt} onChange={(e) => setGlobalFilters({ ...globalFilters, updatedAt: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={globalFilters.status} onChange={(e) => setGlobalFilters({ ...globalFilters, status: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white">
                  <option value="">All</option>
                  <option value="INITIATED">Initiated</option>
                  <option value="INPROGRESS">In Progress</option>
                  <option value="CLOSEDBYADMIN">Closed by Admin</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Reset</button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditPopup && editRequestData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowEditPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Update Support Request</h3>
              <button onClick={() => setShowEditPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 grid grid-cols-3 gap-4 text-sm">
              <ReadField label="Customer Name" value={editRequestData?.userId?.fullName} />
              <ReadField label="Mobile" value={editRequestData?.userId?._phoneNumber} />
              <ReadField label="DOB" value={editRequestData?.userId?.dateOfBirth ? editRequestData.userId.dateOfBirth.split("T")[0] : "—"} />
              <ReadField label="Location" value={editRequestData?.userId?.residentLocation} />
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={editRequestData?._status || ""}
                  onChange={(e) => setEditRequestData({ ...editRequestData, _status: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  <option value="INITIATED">Initiated</option>
                  <option value="INPROGRESS">In Progress</option>
                  <option value="CLOSEDBYADMIN">Closed by Admin</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </select>
              </div>
              <div className="col-span-3 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Customer Message</label>
                  <textarea value={editRequestData?.customerMessage || ""} readOnly rows={4} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Response Notes</label>
                  <textarea
                    value={editRequestData?._notes || ""}
                    onChange={(e) => setEditRequestData({ ...editRequestData, _notes: e.target.value })}
                    rows={4}
                    placeholder="Add response notes..."
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 resize-none"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowEditPopup(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleEditRequest} disabled={showEditButton} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewRequestPopup && viewRequestData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setViewRequestPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Support Request Details</h3>
              <button onClick={() => setViewRequestPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 grid grid-cols-3 gap-4 text-sm">
              <ReadField label="Customer Name" value={viewRequestData?.userId?.fullName} />
              <ReadField label="Mobile" value={viewRequestData?.userId?._phoneNumber} />
              <ReadField label="DOB" value={viewRequestData?.userId?.dateOfBirth ? viewRequestData.userId.dateOfBirth.split("T")[0] : "—"} />
              <ReadField label="Location" value={viewRequestData?.userId?.residentLocation} />
              <ReadField label="Status" value={viewRequestData?._status} />
              <div className="col-span-3 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Customer Message</label>
                  <textarea value={viewRequestData?.customerMessage || "—"} readOnly rows={4} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Response Notes</label>
                  <textarea value={viewRequestData?._notes || "—"} readOnly rows={4} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button onClick={() => setViewRequestPopup(false)} className="w-full py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReadField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">{value || "—"}</div>
  </div>
);

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default SupportListing;
